import React from "react";
import { Descriptions, Tag, Card, Empty } from "antd";
import { ArrowRightOutlined, DownloadOutlined } from "@ant-design/icons";
import trans, { formatDateTime } from "../../../utils";

/**
 * One revision of a submittal, mirroring InspectionRequestRevisions.jsx.
 *
 * Presentational only: the parent owns the revision array and the selected index,
 * exactly as InspectonRequestPopup.jsx does for IRs. Field names are camelCase here
 * because the submittal API differs from backend1's snake_case IR payload.
 */

const reviewResultColors = {
	APPROVED: "green",
	RETURNED: "default",
	REJECTED: "red",
	REVISION_REQUIRED: "purple",
};

// Same helpers SubmittalsInf.jsx defines locally (lines 27-47); kept local here too
// rather than refactoring a pattern that is repeated across the app.
const getUrl = (type) => {
	const lowerCaseType = (type || "").toLowerCase();
	if (lowerCaseType === "image/png") {
		return `${process.env.PUBLIC_URL}/assets/PNG.svg`;
	} else if (lowerCaseType === "image/jpg" || lowerCaseType === "image/jpeg") {
		return `${process.env.PUBLIC_URL}/assets/JPG.svg`;
	} else if (lowerCaseType === "application/pdf") {
		return `${process.env.PUBLIC_URL}/assets/PDF.svg`;
	}
	return `${process.env.PUBLIC_URL}/assets/Excel.svg`;
};

const truncateItem = (text) => {
	const maxLength = 50;
	return text?.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

export default function SubmittalsRevisions({
	data = [],
	currentRevisionIndex,
	statusColors = {},
}) {
	const currentIndex =
		currentRevisionIndex !== undefined && currentRevisionIndex !== null
			? currentRevisionIndex
			: data.length > 0
				? data.length - 1
				: 0;
	const revision = data?.[currentIndex];

	if (!revision) {
		return <Empty description={trans("No revisions found")} />;
	}

	const items = [
		{
			key: "revision",
			label: trans("Revision"),
			children: (
				<Tag color="green">
					{revision.revisionType}-Rev.{revision.revisionNumber}
				</Tag>
			),
		},
		{
			key: "status",
			label: trans("Status"),
			children: (
				<span className="d-flex align-items-center gap-2 flex-wrap">
					<Tag color={statusColors[revision.status] || "default"}>
						{trans(revision.status)}
					</Tag>
					{revision.reviewResult && (
						<>
							<ArrowRightOutlined />
							<Tag color={reviewResultColors[revision.reviewResult] || "default"}>
								{trans(revision.reviewResult)}
							</Tag>
						</>
					)}
				</span>
			),
		},
		{
			key: "submittedAt",
			label: trans("Submitted At"),
			children: revision.submittedAt
				? formatDateTime(revision.submittedAt)
				: "-",
		},
		{
			key: "submittedBy",
			label: trans("Submitted By"),
			children: revision.submittedBy?.name || "-",
		},
		{
			key: "reviewedAt",
			label: trans("Reviewed At"),
			children: revision.reviewedAt
				? formatDateTime(revision.reviewedAt)
				: trans("In Progress"),
		},
		{
			key: "reviewedBy",
			label: trans("Reviewed By"),
			children: revision.reviewedBy?.name || "-",
		},
	];

	if (revision.turnaroundHours !== null && revision.turnaroundHours !== undefined) {
		items.push({
			key: "turnaround",
			label: trans("Turnaround"),
			children: `${revision.turnaroundHours} ${trans("hours")}`,
		});
	}
	if (revision.description) {
		items.push({
			key: "description",
			label: trans("Description"),
			children: revision.description,
		});
	}
	if (revision.correctionNotes) {
		items.push({
			key: "correctionNotes",
			label: trans("Correction Notes"),
			children: revision.correctionNotes,
		});
	}
	if (revision.reviewComments) {
		items.push({
			key: "reviewComments",
			label: trans("Review Comments"),
			children: revision.reviewComments,
		});
	}

	const attachments = revision.attachments || [];

	return (
		<div className="d-flex flex-column gap-3">
			<Descriptions
				title={`${trans("Revision Information")} (${trans("Revision")} ${
					currentIndex + 1
				} ${trans("of")} ${data.length})`}
				bordered
				size="small"
				column={1}
				labelStyle={{ width: "200px" }}
				items={items}
			/>

			<Card
				size="small"
				title={`${trans("Attachments")} (${attachments.length})`}
			>
				{attachments.length === 0 ? (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={trans("No attachments")}
					/>
				) : (
					<div className="d-flex flex-column gap-3">
						{attachments.map((file) => (
							<div
								key={file.id}
								className="attachments p-3 rounded-2xl shadow-md bg-white hover:shadow-lg transition-shadow duration-300 flex items-center justify-between flex-between"
							>
								{/* Left side: file info */}
								<div className="flex items-center gap-3">
									<img
										src={getUrl(file.type)}
										width={40}
										height={40}
										alt="format"
										className="rounded-md"
									/>
									<div className="flex flex-col">
										<a
											href={file.url}
											target="_blank"
											rel="noopener noreferrer"
											className="text-sm font-medium text-gray-800 hover:text-blue-600 truncate max-w-[400px]"
											title={file.name}
										>
											{truncateItem(file.name)}
										</a>
										<div className="text-xs text-gray-500 mt-1">
											{file.size} MB
										</div>
									</div>
								</div>

								{/* Right side: who uploaded it and when */}
								<div className="flex flex-col items-end gap-1">
									<div className="text-xs text-gray-400">
										{file?.uploadedBy?.name || "Unknown"}
									</div>
									<div className="text-xs text-gray-400">
										{file?.uploadedAt
											? new Date(file.uploadedAt).toLocaleString()
											: ""}
									</div>
									<a
										href={file.url}
										target="_blank"
										rel="noopener noreferrer"
										className="mt-1"
									>
										<DownloadOutlined />
									</a>
								</div>
							</div>
						))}
					</div>
				)}
			</Card>
		</div>
	);
}
